import { Notice } from '@/components/ui/Notice';

/**
 * Summary shown at the top of a catalog form after a failed save. The form-level
 * message carries what the user can act on ("A product with that product code
 * already exists", or the key a `.strict()` schema rejected); the count says how
 * many inputs are marked, since the offending one may be scrolled out of view.
 *
 * ⚠️ **`fieldErrorCount` must count only errors bound to a control this form
 * actually renders.** Claiming a field is "highlighted below" when nothing is
 * fails invisibly. `ApiError.routeFieldErrors(knownKeys)` draws that line: count
 * its `fields`, pass its `unroutable` as `formError`. An empty `path` is not the
 * only unbindable one — `cityId` looks bindable but isn't when the control is
 * `cityIds`.
 *
 * ⚠️ **Both halves render when both are present, deliberately** — the commonest
 * failure pairs a strict rejection with a genuine field error, and either alone
 * would hide the other.
 */
export function FormErrorSummary({
  formError,
  fieldErrorCount,
}: {
  formError: string | null;
  fieldErrorCount: number;
}) {
  if (!formError && fieldErrorCount === 0) return null;

  const highlighted =
    fieldErrorCount === 0
      ? null
      : fieldErrorCount === 1
        ? '1 field needs attention below.'
        : `${fieldErrorCount} fields need attention below.`;

  return (
    <Notice tone="danger" role="alert" className="break-words">
      <span className="block min-w-0 space-y-1">
        {formError && <span className="block">{formError}</span>}
        {highlighted && <span className="block">{highlighted}</span>}
      </span>
    </Notice>
  );
}
